import React, { useState, useEffect } from "react";
import { NotificationContainer, NotificationManager } from "react-notifications";
import { useNavigate } from "react-router-dom";
import DashboardHeader from "../DashboardHeader/DashboardHeader";
import StepHeader from "../StepHeader/StepHeader";
import ActionBar from "../ActionBar/ActionBar";
import { APP_STEPS } from "../config/steps";
import "./AcademicInfo.css";
import WhatsAppWidget from "../WhatsAppWidget/WhatsAppWidget";

const AcademicInformation = () => {
  // ---- state (existing field names kept) ----
  const [rows, setRows] = useState([
    {
      id: null,
      applicantId: null,
      name: 0,
      fos: null,
      institute: null,
      result_type: "percentage",
      cgpa: null,
      percentage: null,
      sgpa: null,
      on_going: false,
      yearend: 0,
      yearstart: 0,
      semester: null,
    },
  ]);
  const [loading, setLoading] = useState(true);
  const [errors, setErrors] = useState([]);
  const [saving, setSaving] = useState(false);

  // ---- marksheet upload state ----
  const [marksheetFile, setMarksheetFile] = useState(null);
  const [marksheetData, setMarksheetData] = useState(null);
  const [marksheetId, setMarksheetId] = useState(null);
  const [marksheetInputKey, setMarksheetInputKey] = useState(Date.now());
  const [removing, setRemoving] = useState(false);

  // Accepted marksheet types — kept in sync with the Documents upload step
  const MARKSHEET_ACCEPT = ".pdf,.png,.jpeg,.jpg";
  const MARKSHEET_MIME = ["application/pdf", "image/png", "image/jpeg", "image/jpg"];
  const MARKSHEET_MAX_SIZE = 5000000; // 5 MB

  const navigate = useNavigate();

  const token = localStorage.getItem("token");
  const applicationId = parseInt(localStorage.getItem("id"), 10);
  const status = localStorage.getItem("application_status");
  const isDisabled = status !== "incomplete";

  const currentStep = 1; // index in APP_STEPS

  // years for dropdown
  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: currentYear - 1998 }, (v, i) => ({
    id: i + 1,
    year: 1999 + i,
  }));

  // ---- fetch existing academic details + marksheet ----
  useEffect(() => {
    const fetchData = async () => {
      try {
        const campaignRes = await fetch(
          `${process.env.REACT_APP_BASE_URL_strapi}/campaigns?sort=createdAt:desc&pagination[limit]=1`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        const campaignResponse = await campaignRes.json();
        setLoading(false);

        const latestCampaign = campaignResponse.data[0];
        if (!latestCampaign) return;

        // Fetch academic details
        const academicRes = await fetch(
          `${process.env.REACT_APP_BASE_URL_strapi}/academic-details?&filters[application][id][$eq]=${applicationId}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        const academic_details = await academicRes.json();
        // Strapi wraps content-api entries in `attributes` — unwrap defensively
        // so this works whether or not the response is wrapped.
        const academicdetails = (academic_details.data || []).map((d) => ({
          ...(d.attributes || d),
          id: d.id,
          documentId: d.documentId,
        }));

        if (academicdetails.length > 0) {
          const transformedRows = academicdetails.map((detail) => ({
            on_going: detail.on_going,
            name: detail.degree,
            yearend: detail.end_year,
            fos: detail.field_of_study,
            institute: detail.institute,
            result_type: detail.cgpa ? "cgpa" : "percentage",
            cgpa: detail.cgpa || null,
            percentage: detail.total_marks || null,
            sgpa: detail.sgpa,
            yearstart: detail.start_year,
            id: detail.id,
            documentId: detail.documentId,
          }));
          setRows(transformedRows);
        }

        // Fetch marksheet
        const marksheetRes = await fetch(
          `${process.env.REACT_APP_BASE_URL_strapi}/academic-uploads?&filters[application][id][$eq]=${applicationId}&populate=marksheet`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        const marksheetResult = await marksheetRes.json();
        const marksheets = (marksheetResult.data || []).map((d) => ({
          ...(d.attributes || d),
          id: d.id,
        }));

        // One application can end up with several academic_uploads rows — each
        // submit used to create a new one. The current marksheet lives on the
        // most recently updated row that actually holds a file; fall back to the
        // most recently updated row so we reuse it instead of adding another.
        const ordered = [...marksheets].sort(
          (a, b) => new Date(b.updatedAt) - new Date(a.updatedAt)
        );
        const marksheetRow = ordered.find((m) => m.marksheet) || ordered[0];

        if (marksheetRow) {
          setMarksheetId(marksheetRow.id);
        }

        if (marksheetRow?.marksheet) {
          setMarksheetData({
            // media file id — needed to delete the file from the media library
            id: marksheetRow.marksheet.id,
            path: marksheetRow.marksheet.url,
            name: marksheetRow.marksheet.name,
          });
        }
      } catch (err) {
        console.error("Error fetching academic details:", err);
      }
    };
    fetchData();
  }, [applicationId, token]);

  // ---- marksheet file handlers ----
  const handleMarksheetChange = (e) => {
    if (e.target.files.length > 0) {
      const file = e.target.files[0];
      // Some browsers report an empty type for images — fall back to the extension
      const isAllowedType =
        MARKSHEET_MIME.includes(file.type) || /\.(pdf|png|jpe?g)$/i.test(file.name);

      if (file.size <= MARKSHEET_MAX_SIZE && isAllowedType) {
        setMarksheetFile(file);
      } else {
        NotificationManager.error("File must be a JPG, PNG or PDF and less than 5MB");
        setMarksheetInputKey(Date.now());
      }
    }
  };

  // Remove the marksheet attachment — both the freshly picked file and the
  // one already saved in Strapi (the ✕ used to only clear the local file,
  // so an already-uploaded marksheet looked like it never got removed).
  const handleMarksheetRemove = async () => {
    if (isDisabled) return;

    const savedFileId = marksheetData?.id;
    const savedEntryId = marksheetId;

    // Optimistically reset the UI so the control responds immediately
    setMarksheetFile(null);
    setMarksheetData(null);
    setMarksheetId(null);
    setMarksheetInputKey(Date.now());
    localStorage.removeItem("marksheet");

    if (!savedFileId && !savedEntryId) return;

    setRemoving(true);
    try {
      if (savedEntryId) {
        const res = await fetch(
          `${process.env.REACT_APP_BASE_URL_strapi}/academic-uploads/${savedEntryId}`,
          { method: "DELETE", headers: { Authorization: `Bearer ${token}` } }
        );
        if (!res.ok) throw new Error("Failed to delete marksheet entry");
      }

      // Best effort: also drop the file from the media library so it is not orphaned
      if (savedFileId) {
        try {
          await fetch(
            `${process.env.REACT_APP_BASE_URL_strapi}/upload/files/${savedFileId}`,
            { method: "DELETE", headers: { Authorization: `Bearer ${token}` } }
          );
        } catch (fileErr) {
          console.warn("Marksheet file cleanup failed:", fileErr);
        }
      }

      NotificationManager.success("Marksheet removed");
    } catch (err) {
      console.error("Error removing marksheet:", err);
      NotificationManager.error("Could not remove the marksheet. Please try again.");
    } finally {
      setRemoving(false);
    }
  };

  // ---- input change ----
  const handleInputChange = (index, field, value) => {
    setRows(
      rows.map((row, i) => {
        if (i !== index) return row;
        let newRow = { ...row, [field]: value };
        if (field === "yearstart") {
          newRow.yearend = newRow.yearend > value ? newRow.yearend : 0;
        } else if (field === "yearend") {
          newRow.yearstart = newRow.yearstart < value ? newRow.yearstart : 0;
        } else if (field === "result_type") {
          // clear the other field when switching result type
          if (value === "percentage") {
            newRow.cgpa = null;
          } else {
            newRow.percentage = null;
          }
        }
        return newRow;
      })
    );
  };

  // ---- validation (existing rules kept) ----
  const validateRow = (row) => {
    const rowErrors = {};
    if (!row.name) rowErrors.name = "Degree is required";
    if (!row.institute) rowErrors.institute = "Institute is required";
    if (!row.fos) rowErrors.fos = "Field of Study is required";
    if (!row.yearstart) rowErrors.yearstart = "Start Year is required";
    if (!row.on_going && !row.yearend) rowErrors.yearend = "End Year is required if not ongoing";
    if (row.result_type === "percentage" && !row.percentage)
      rowErrors.percentage = "Percentage is required";
    if (row.result_type === "cgpa" && !row.cgpa)
      rowErrors.cgpa = "CGPA / Grade is required";
    if (
      row.on_going &&
      (row.name === "Bachelors" || row.name === "Masters") &&
      !row.semester
    )
      rowErrors.semester = "Semester Selection Is Required";
    return rowErrors;
  };

  const validateForm = () => {
    const newErrors = rows.map(validateRow);
    setErrors(newErrors);
    return newErrors.every((e) => Object.keys(e).length === 0);
  };

  // ---- submit (existing API logic kept) ----
  const handleSubmit = async () => {
    if (isDisabled) return;
    if (status === "new") { navigate("/application/finance-details"); return; }
    if (!validateForm()) return;
    setSaving(true);

    const academicData = rows.map((row) => ({
      degree: row.name,
      field_of_study: row.fos,
      institute: row.institute,
      // total_marks is a bigint field in Strapi — decimals are not allowed, so round to integer
      total_marks:
        row.result_type === "percentage" && row.percentage !== "" && row.percentage !== null
          ? Math.round(parseFloat(row.percentage))
          : null,
      cgpa: row.result_type === "cgpa" ? row.cgpa : null,
      sgpa: row.sgpa,
      semester: row.semester,
      on_going: row.on_going,
      end_year: parseInt(row.yearend),
      start_year: parseInt(row.yearstart),
      id: row.id || null,
      documentid: row.documentId,
    }));

    try {
      for (let i = 0; i < academicData.length; i++) {
        const currentRow = academicData[i];
        if (currentRow.id) {
          const { id, documentid, ...updateData } = currentRow;
          const response = await fetch(
            `${process.env.REACT_APP_BASE_URL_strapi}/academic-details/${currentRow.documentid}`,
            {
              method: "PUT",
              headers: {
                Authorization: `Bearer ${token}`,
                "Content-Type": "application/json",
              },
              body: JSON.stringify({ data: updateData }),
            }
          );
          if (!response.ok) throw new Error("Failed to update academic detail");
        } else {
          delete currentRow.id;
          const response = await fetch(
            `${process.env.REACT_APP_BASE_URL_strapi}/academic-details`,
            {
              method: "POST",
              headers: {
                Authorization: `Bearer ${token}`,
                "Content-Type": "application/json",
              },
              body: JSON.stringify({ data: { ...currentRow, applicationId } }),
            }
          );
          if (!response.ok) throw new Error("Failed to create academic detail");
          const createdData = await response.json();
          localStorage.setItem("acedemic_detail", createdData.id);
        }
      }
      // Upload marksheet if selected
      if (marksheetFile) {
        const msForm = new FormData();
        msForm.append("file", marksheetFile);
        msForm.append("application_id", applicationId);

        let msResponse;
        if (marksheetId) {
          msForm.append("application_id", applicationId);
          msForm.append("marksheet", marksheetFile);
          msResponse = await fetch(
            `${process.env.REACT_APP_BASE_URL_strapi}/academic-uploads/${marksheetId}`,
            { method: "PUT", headers: { Authorization: `Bearer ${token}` }, body: msForm }
          );
        } else {
          msResponse = await fetch(
            `${process.env.REACT_APP_BASE_URL_strapi}/academic-uploads`,
            { method: "POST", headers: { Authorization: `Bearer ${token}` }, body: msForm }
          );
        }

        if (msResponse.ok) {
          const msData = await msResponse.json();
          const fileInfo = msData[0] || msData;
          localStorage.setItem("marksheet", fileInfo.id);
        }
      }

      setSaving(false);
      // Navigate to finance details (marksheet is now part of academic step)
      navigate("/application/finance-details");
    } catch (error) {
      console.error("Error submitting academic details:", error);
      setSaving(false);
      NotificationManager.error("Failed to save academic details. Please try again.");
    }
  };

  // ---- helper: field error class ----
  const fieldClass = (index, field) =>
    `ai__field${errors[index]?.[field] ? " invalid" : ""}`;

  return (
    <div>
      {/* Top bar */}
      <DashboardHeader />

      {/* Step progress */}
      <div style={{ background: "rgba(255,255,255,.97)", borderBottom: "1px solid #dbe3e1" }}>
        <div className="ai" style={{ paddingBottom: 0 }}>
          <StepHeader steps={APP_STEPS} current={currentStep} />
        </div>
      </div>

      {/* Form */}
      <div className="ai">
        {loading ? (
          <div style={{ display: "flex", justifyContent: "center", padding: "40px 0" }}>
            <div className="spinner-bubble spinner-bubble-primary"></div>
          </div>
        ) : (
          <form onSubmit={(e) => { e.preventDefault(); handleSubmit(); }}>
            {/* page heading */}
            <h2 className="ai__title">Academic details</h2>
            <p className="ai__sub">Your most recent academic information.</p>
            <p className="ai__req-note">Fields marked <em className="ai__req">*</em> are required.</p>

            {/* ---- Card: Academic details ---- */}
            {rows.map((row, index) => (
              <div key={index}>
                <div className="ai__card">
                  {index > 0 && (
                    <div className="ai__section-label">Academic Entry #{index + 1}</div>
                  )}

                  {/* Degree */}
                  <div className={fieldClass(index, "name")}>
                    <label className="ai__label">Degree <em className="ai__req">*</em></label>
                    <select
                      className="ai__select"
                      name="name"
                      value={row.name || 0}
                      onChange={(e) => handleInputChange(index, "name", e.target.value)}
                      disabled={isDisabled}
                    >
                      <option value={0}>Please Select Degree</option>
                      <option value="College">College</option>
                      <option value="School">School</option>
                      <option value="Bachelors">Bachelors</option>
                      <option value="Masters">Masters</option>
                      <option value="Doctorate">Doctorate</option>
                    </select>
                    <p className="ai__err">{errors[index]?.name}</p>
                  </div>

                  {/* Institute */}
                  <div className={fieldClass(index, "institute")}>
                    <label className="ai__label">Institute <em className="ai__req">*</em></label>
                    <input
                      className="ai__input"
                      placeholder="University / college name"
                      value={row.institute || ""}
                      onChange={(e) => handleInputChange(index, "institute", e.target.value)}
                      disabled={isDisabled}
                    />
                    <p className="ai__err">{errors[index]?.institute}</p>
                  </div>

                  {/* Field of Study */}
                  <div className={fieldClass(index, "fos")}>
                    <label className="ai__label">Field of Study <em className="ai__req">*</em></label>
                    <input
                      className="ai__input"
                      placeholder="e.g. Computer Science"
                      value={row.fos || ""}
                      onChange={(e) => handleInputChange(index, "fos", e.target.value)}
                      disabled={isDisabled}
                    />
                    <p className="ai__err">{errors[index]?.fos}</p>
                  </div>

                  {/* Start Year + End Year */}
                  <div className="ai__row2">
                    <div className={fieldClass(index, "yearstart")}>
                      <label className="ai__label">Start Year <em className="ai__req">*</em></label>
                      <select
                        className="ai__select"
                        name="yearstart"
                        value={row.yearstart || 0}
                        onChange={(e) => handleInputChange(index, "yearstart", e.target.value)}
                        disabled={isDisabled}
                      >
                        <option value={0}>Select Start Year</option>
                        {years.map((year) => (
                          <option key={year.id} value={year.year}>{year.year}</option>
                        ))}
                      </select>
                      <p className="ai__err">{errors[index]?.yearstart}</p>
                    </div>

                    {!row.on_going && (
                      <div className={fieldClass(index, "yearend")}>
                        <label className="ai__label">End Year <em className="ai__req">*</em></label>
                        <select
                          className="ai__select"
                          name="yearend"
                          value={row.yearend || 0}
                          onChange={(e) => handleInputChange(index, "yearend", e.target.value)}
                          disabled={isDisabled}
                        >
                          <option value={0}>Select End Year</option>
                          {years
                            .filter((year) => year.year >= row.yearstart)
                            .map((year) => (
                              <option key={year.id} value={year.year}>{year.year}</option>
                            ))}
                        </select>
                        <p className="ai__err">{errors[index]?.yearend}</p>
                      </div>
                    )}
                  </div>

                  {/* On-going checkbox */}
                  <div className="ai__field">
                    <label className="ai__check">
                      <input
                        type="checkbox"
                        name="on_going"
                        checked={row.on_going || false}
                        onChange={(e) => handleInputChange(index, "on_going", e.target.checked)}
                        disabled={isDisabled}
                      />
                      <span>I am still studying (on-going)</span>
                    </label>
                  </div>

                  {/* Semester (only for Bachelors/Masters ongoing) */}
                  {row.on_going && (row.name === "Bachelors" || row.name === "Masters") && (
                    <div className={fieldClass(index, "semester")}>
                      <label className="ai__label">Semester <em className="ai__req">*</em></label>
                      <select
                        className="ai__select"
                        name="semester"
                        value={row.semester || 0}
                        onChange={(e) => handleInputChange(index, "semester", e.target.value)}
                        disabled={isDisabled}
                      >
                        <option value={0}>Please Select Semester</option>
                        {[1,2,3,4,5,6,7,8].map((s) => (
                          <option key={s} value={s}>Semester {s}</option>
                        ))}
                      </select>
                      <p className="ai__err">{errors[index]?.semester}</p>
                    </div>
                  )}

                  {/* SGPA + CGPA (Bachelors/Masters ongoing) */}
                  {row.on_going && (row.name === "Bachelors" || row.name === "Masters") && (
                    <div className="ai__row2">
                      <div className={fieldClass(index, "sgpa")}>
                        <label className="ai__label">Previous Semester SGPA</label>
                        <input
                          className="ai__input"
                          placeholder="Enter SGPA"
                          value={row.sgpa || ""}
                          onChange={(e) => handleInputChange(index, "sgpa", e.target.value)}
                          disabled={isDisabled}
                        />
                        <p className="ai__err">{errors[index]?.sgpa}</p>
                      </div>
                      <div className={fieldClass(index, "cgpa")}>
                        <label className="ai__label">CGPA</label>
                        <input
                          className="ai__input"
                          placeholder="Enter CGPA"
                          value={row.cgpa || ""}
                          onChange={(e) => handleInputChange(index, "cgpa", e.target.value)}
                          disabled={isDisabled}
                        />
                        <p className="ai__err">{errors[index]?.cgpa}</p>
                      </div>
                    </div>
                  )}

                </div>

                {/* ---- Card: Latest result (separate card, like reference HTML) ---- */}
                <div className="ai__card">
                  <h3>Latest result</h3>

                  {/* Result type */}
                  <div className="ai__field">
                    <div className="ai__lbl">Result type</div>
                    <div className="ai__seg">
                      <label>
                        <input
                          type="radio"
                          name={`resType_${index}`}
                          value="percentage"
                          checked={row.result_type === "percentage"}
                          onChange={() => handleInputChange(index, "result_type", "percentage")}
                          disabled={isDisabled}
                        />
                        <span>Percentage</span>
                      </label>
                      <label>
                        <input
                          type="radio"
                          name={`resType_${index}`}
                          value="cgpa"
                          checked={row.result_type === "cgpa"}
                          onChange={() => handleInputChange(index, "result_type", "cgpa")}
                          disabled={isDisabled}
                        />
                        <span>CGPA</span>
                      </label>
                    </div>
                  </div>

                  {/* Percentage field — only when result_type = percentage */}
                  {row.result_type === "percentage" && (
                    <div className={fieldClass(index, "percentage")}>
                      <label className="ai__label">Percentage <em className="ai__req">*</em></label>
                      <input
                        className="ai__input"
                        inputMode="decimal"
                        placeholder="e.g. 78.5"
                        value={row.percentage || ""}
                        onChange={(e) => {
                          const val = e.target.value;
                          if (val === "" || (Number(val) >= 0 && Number(val) <= 100)) {
                            handleInputChange(index, "percentage", val);
                          }
                        }}
                        disabled={isDisabled}
                      />
                      <p className="ai__hint">Decimals will be rounded to the nearest whole number (e.g. 84.23 &rarr; 84)</p>
                      <p className="ai__err">{errors[index]?.percentage}</p>
                    </div>
                  )}

                  {/* Division / Grade / CGPA field — only when result_type = cgpa */}
                  {row.result_type === "cgpa" && (
                    <div className={fieldClass(index, "cgpa")}>
                      <label className="ai__label">CGPA</label>
                      <input
                        className="ai__input"
                        inputMode="decimal"
                        placeholder="Enter CGPA or Grade"
                        value={row.cgpa || ""}
                        onChange={(e) => handleInputChange(index, "cgpa", e.target.value)}
                        disabled={isDisabled}
                      />
                      <p className="ai__err">{errors[index]?.cgpa}</p>
                    </div>
                  )}

                  {/* Marksheet Upload — AFTER result fields */}
                  <div className={fieldClass(index, "marksheet")}>
                    <label className="ai__label">Upload your latest marksheet <em className="ai__req">*</em></label>
                    <div className="ai__note">
                      <span>&#9888;&#65039;</span>
                      <span>
                        If you are uploading an <strong>online result</strong>, it must be <strong>attested by the college or university that issued it</strong>.
                      </span>
                    </div>
                    <div className={`ai__file${marksheetFile || marksheetData?.path ? " has" : ""}`}>
                      <input
                        type="file"
                        id={`marksheetInput_${index}`}
                        accept={MARKSHEET_ACCEPT}
                        onChange={handleMarksheetChange}
                        key={marksheetInputKey}
                        hidden
                        disabled={isDisabled}
                      />

                      {(marksheetFile || marksheetData?.path) ? (
                        <div className="ai__got">
                          <span className="ai__ok">
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3.5">
                              <path d="M5 12l5 5 9-10" />
                            </svg>
                          </span>
                          <span className="ai__meta">
                            <span className="ai__fname">
                              {marksheetFile ? marksheetFile.name : marksheetData?.name || "marksheet.pdf"}
                            </span>
                            {marksheetFile && <small>{(marksheetFile.size / 1024 / 1024).toFixed(2)} MB</small>}
                          </span>
                          <button
                            type="button"
                            className="ai__x"
                            onClick={handleMarksheetRemove}
                            disabled={removing || isDisabled}
                            aria-label="Remove file"
                          >
                            {removing ? "…" : "✕"}
                          </button>
                        </div>
                      ) : (
                        <label htmlFor={`marksheetInput_${index}`} className="ai__pick">
                          <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
                            <path d="M12 16V4M7 9l5-5 5-5" />
                            <path d="M4 16v4h16v-4" />
                          </svg>
                          <span>
                            <b>Tap to upload</b>
                            <small>JPG, PNG or PDF · max 5 MB</small>
                          </span>
                        </label>
                      )}
                    </div>
                    {/* View uploaded marksheet */}
                    {marksheetData?.path && !marksheetFile && (
                      <button
                        type="button"
                        onClick={() => window.open(marksheetData.path, "_blank")}
                        style={{
                          display: "inline-flex", alignItems: "center", gap: "6px",
                          background: "none", border: "1px solid #dfe6e4", borderRadius: "10px",
                          padding: "6px 12px", fontWeight: 600, fontSize: "12px",
                          color: "#115e59", cursor: "pointer", marginTop: "8px",
                        }}
                      >
                        View Uploaded Marksheet
                      </button>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </form>
        )}
      </div>

      {/* Bottom action bar */}
      <ActionBar
        onBack={() => navigate("/application/personal-information")}
        onNext={handleSubmit}
        backLabel="← Back"
        nextLabel="Continue →"
        nextDisabled={isDisabled}
        nextLoading={saving}
      />

      <NotificationContainer />
      <WhatsAppWidget />
    </div>
  );
};

export default AcademicInformation;
